import { memone } from '@deqo/foundation';
import { debounce } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../concepts/objects';
import { ActiveObject, resolve } from '../../Container';
import { AdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Selection } from '../../canvas/Selection';
import { RelativeTo } from '../../canvas/entities/Physical';
import { PropertyHistory } from '../../canvas/properties/PropertyHistory';
import { ToolInfo } from '../../canvas/tooling/ToolInfo';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { RoadmapCard } from './RoadmapCard';
import { RoadmapCardEndDate, RoadmapCardStartDate, RoadmapCardTargetDate } from './RoadmapCardAssocs';
import { RoadmapCardDateMarkerWidget } from './RoadmapCardDateMarkerWidget';

export class RoadmapCardMechanics extends ActiveObject {

    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(PropertyHistory) private propHistory!: PropertyHistory;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    public start(): void {
        const syncDebounced = debounce(this.sync);
        this.burden(this.session.observe(AdapterInfo, syncDebounced));
        this.burden(this.session.observe(Selection, syncDebounced));
        this.burden(this.session.observe(ToolInfo, syncDebounced));
        this.burden(
            this.objects.createTrigger('RoadmapCard.Punctuality')
                .for(RoadmapCard)
                .onChangesTo('punctuality')
                .doForEach((card) => {
                    if (card.punctuality === 'early' && card.marker < 0) {
                        card.marker = card.width - 10;
                    }
                    if (card.punctuality === 'late' && card.marker < 0) {
                        card.marker = card.width;
                        card.width += 10;
                    }
                }),
            this.objects.on('updated', this.onRoadmapCardsUpdated),
        );

        this.onRoadmapCardsUpdated(Array.from(this.objects.getAll(RoadmapCard)));
    }

    private onRoadmapCardsUpdated = (cmpts: Component[]) => {

        for (const c of cmpts) {
            const card = c.as(RoadmapCard);
            if (!card?.alive) continue;

            const bounds = card.bounds(RelativeTo.Model);
            const start = bounds.left;
            const end = card.punctuality === 'early' ? bounds.left + card.marker : bounds.right;
            const target =
                (card.punctuality === 'early' ? bounds.right : null) ??
                (card.punctuality === 'late' ? bounds.left + card.marker : null);

            const timeline = card.timeline();
            RoadmapCardStartDate.setMaybe(card, timeline?.getDateAtPosition(start) ?? null);
            RoadmapCardTargetDate.setMaybe(card, timeline?.getDateAtPosition(target) ?? null);
            RoadmapCardEndDate.setMaybe(card, timeline?.getDateAtPosition(end) ?? null);
        }

        this.sync();
    };

    private sync = () => {
        const instanceId = this.getNextDateMarkerInstanceId();
        this.realizeDateMarkerWidget(instanceId);
    };

    private getNextDateMarkerInstanceId(): string | null {
        const adapter = this.session.get(AdapterInfo);
        const selection = this.session.get(Selection);
        const tool = this.session.get(ToolInfo);
        if (selection.size !== 1) return null;
        if (selection.locked()) return null;
        if (!selection.first().is(RoadmapCard)) return null;
        if (adapter.target !== 'none') return null;
        // if (isToolStateBanned(tool.state)) return null;
        return selection.id;
    }

    @memone() // This acts as a call guard (only run if the args change)
    private realizeDateMarkerWidget(instanceId?: string) {

        const existing = this.widgets.get('DateMarker');
        if (existing?.tag === instanceId) return;

        this.widgets.delete('DateMarker');
        if (!instanceId) return;

        const targets = this.session.get(Selection).toArray();

        const dock = this.widgets.createContainer({
            name: 'DateMarker',
            position: ['unmanaged'],
            style: 'none',
        });

        dock.add(this.widgets.createInline({
            ui: RoadmapCardDateMarkerWidget,
        }));

        this.widgets.add(dock);
    };
}
